/**
 * Pure reducer from a `/today` (or `/status`) response envelope to the
 * SPA's reduced `UiState`. Replaces the 140-line `switch` in the legacy
 * monolith plus the equivalent branches inside `checkStatus`.
 *
 * Hard rules:
 *   - No I/O, no React, no `setState`. Data in → `UiState` out.
 *   - Branches are exhaustive over `GenerationStatus`. Adding a new value
 *     to that union should force a new branch here too (the test suite
 *     `aotdStateMachine.test.ts` is the gate).
 */
import type {
  AotdProgressSnapshot,
  ArtInfo,
  GenerationStatus,
  StatusResponse,
  TodayResponse,
} from "../api/types";
import type { UiState } from "./types";

export interface ReduceContext {
  isAuthenticated: boolean;
  previousArt?: ArtInfo;
  artUrl?: string | null;
}

/**
 * How long past the day's `scheduledTime` the birthday user keeps first-claim
 * priority. Mirrors `BIRTHDAY_PRIORITY_DELAY_MS` in the backend's
 * `services/hooks/handlers/birthdayPriorityHandler.ts`. Only used as a fallback
 * when the server response carries no `unlockAt` — never as the primary source,
 * because the unlock hour moves with the day's `utcOffset` (04:50 UTC + 0..12h).
 */
const BIRTHDAY_PRIORITY_DELAY_MS = 450 * 1000;

type RawResponse = (TodayResponse | StatusResponse) & {
  scheduledTime?: string;
  msEarly?: number;
  retryAfterMs?: number;
  blockingOwnerUsername?: string;
  blockingCardSlug?: string;
  blockingCardName?: string;
  sourceCardSlug?: string;
  sourceCardName?: string;
  sourceUsername?: string;
  unlockAt?: string;
  winner?: { username?: string };
  deltaMs?: number;
};

export function reduceTodayResponse(
  response: TodayResponse | StatusResponse | null | undefined,
  ctx: ReduceContext,
): UiState {
  if (!response || typeof response !== "object" || !response.generationStatus) {
    return { kind: "error", message: "Received an invalid response from the server." };
  }

  const data = response as RawResponse;
  const status: GenerationStatus = data.generationStatus;
  const message = data.message ?? "";
  const generationElapsedTime = data.generationElapsedTime;

  switch (status) {
    case "completed": {
      const art: ArtInfo = stripArtInfo(data);
      return {
        kind: "completed",
        art,
        artUrl: ctx.artUrl ?? null,
        hasPermission: Boolean(data.hasPermission),
        generationElapsedTime,
      };
    }
    case "generating":
    case "pending":
    case "started":
    case "busy":
    case "resuming":
      return {
        kind: "generating",
        phase: data.phase,
        participantCount: data.participantCount,
        participantEmojis: data.participantEmojis,
        racers: data.racers,
        message,
        generationStatus: status,
        ...progressToStepFields(data.progress),
      };
    case "not_started": {
      if (!data.scheduledTime) {
        return { kind: "not_started_hidden" };
      }
      return {
        kind: "not_started_scheduled",
        scheduledTime: data.scheduledTime,
        previousArt: ctx.previousArt,
        artUrl: ctx.artUrl ?? null,
      };
    }
    case "unverified":
      return { kind: "unverified" };
    case "birthday_priority": {
      // The hold lasts until `scheduledTime + 450s`, and `scheduledTime` is
      // 04:50 UTC shifted by the day's utcOffset — so the end of the window is
      // whatever the server says it is. Never assume a fixed wall-clock hour.
      const scheduledMs = data.scheduledTime ? Date.parse(data.scheduledTime) : NaN;
      const unlockAt =
        data.unlockAt ??
        (Number.isFinite(scheduledMs)
          ? new Date(scheduledMs + BIRTHDAY_PRIORITY_DELAY_MS).toISOString()
          : undefined);
      return {
        kind: "birthday_priority",
        birthdayUsers: data.usersWithBirthdays ?? [],
        unlockAt,
      };
    }
    case "claim_blocked":
      return {
        kind: "claim_blocked",
        ownerUsername: data.blockingOwnerUsername ?? data.sourceUsername,
        cardSlug: data.blockingCardSlug ?? data.sourceCardSlug,
        cardName: data.blockingCardName ?? data.sourceCardName,
        unlockAt: data.unlockAt,
        message,
      };
    case "entered_early": {
      const raw = Number(data.msEarly);
      const msEarly = Number.isFinite(raw) ? Math.max(0, Math.round(raw)) : 0;
      return {
        kind: "entered_early",
        msEarly,
        scheduledTime: data.scheduledTime,
      };
    }
    case "race_lost": {
      // The user lost the race — but we deliberately DON'T reveal that yet.
      // The whole point of AOTD is the suspense, and the loser short-circuits
      // out of the backend race loop as early as tick 1 (~1–2s), so a blunt
      // "alice entered 50ms before you" reveal lands almost instantly and kills
      // it. Instead we render the exact same suspense screen everyone else sees
      // ("N users think they got the aotd" + the live pipeline progress, all
      // fed by the /status poller + WS day-broadcast). The poller then flips us
      // to the winner's completed art, and *that* is the reveal: the loser sees
      // who actually authored it. Participant counts arrive from /status + WS —
      // the race_lost body carries none — so we leave them undefined here.
      // generationStatus is reported as "generating" so the suspense UI
      // (RaceWaitingIndicator, rotating flavour text) treats it as live.
      return {
        kind: "generating",
        phase: data.phase,
        participantCount: data.participantCount,
        participantEmojis: data.participantEmojis,
        racers: data.racers,
        message: "",
        generationStatus: "generating",
        // The progress snapshot is identity-neutral, so hydrating it here
        // leaks nothing — losers see the same pipeline steps everyone does.
        ...progressToStepFields(data.progress),
      };
    }
    case "unauthorized":
      return { kind: "no_art_or_unauthorized", isAuthenticated: false };
    case "no_art":
      return { kind: "no_art_or_unauthorized", isAuthenticated: ctx.isAuthenticated };
    case "forbidden":
    case "not_found":
      return {
        kind: "error",
        message: message || (status === "forbidden" ? "Forbidden." : "Not found."),
      };
    case "rate_limited":
      return {
        kind: "rate_limited",
        message: message || "Too many refreshes. Please wait a minute.",
      };
    case "failed":
      return { kind: "failed", message: message || data.error || "Generation failed." };
    case "error":
      return { kind: "error", message: message || data.error || "An unknown error occurred." };
    default: {
      // Exhaustiveness check — adding a new GenerationStatus value without a
      // branch above will trip this at compile time.
      const _exhaustive: never = status;
      void _exhaustive;
      return { kind: "error", message: "Received an unexpected status from the server." };
    }
  }
}

/**
 * Map the persisted progress snapshot (REST hydration on generating payloads;
 * see backend `ProgressReporter`) onto the generating UiState's step fields.
 * The WS feed stays primary — `useAotdRace` overlays live WS step fields on
 * top of these, so they only show for a page opened mid-generation before the
 * first WS tick lands. `currentStep` is 1-based in the persisted subdoc
 * (0 = pipeline started, no step yet); the UI's `stepIndex` is 0-based.
 * Tolerates a missing/null snapshot — older backends don't send one.
 */
function progressToStepFields(
  progress: AotdProgressSnapshot | null | undefined,
): Pick<
  Extract<UiState, { kind: "generating" }>,
  "stepLabel" | "stepName" | "stepIndex" | "totalSteps" | "percent"
> {
  if (!progress || typeof progress !== "object") return {};
  const { totalSteps, currentStep, stepName, label, percent } = progress;
  return {
    stepLabel: typeof label === "string" && label ? label : undefined,
    stepName: typeof stepName === "string" && stepName ? stepName : undefined,
    stepIndex:
      typeof currentStep === "number" && currentStep >= 1 ? currentStep - 1 : undefined,
    totalSteps: typeof totalSteps === "number" ? totalSteps : undefined,
    percent: typeof percent === "number" ? percent : undefined,
  };
}

function stripArtInfo(data: RawResponse): ArtInfo {
  return {
    aotdId: data.aotdId,
    title: data.title,
    createdBy: data.createdBy,
    authorQuote: data.authorQuote,
    quoteHelp: data.quoteHelp,
    quoteWindowEndsAt: data.quoteWindowEndsAt,
    currentStreakNum: data.currentStreakNum,
    nextAOTDEmojis: data.nextAOTDEmojis,
    thisAOTDEmojis: data.thisAOTDEmojis,
    emojisHidden: data.emojisHidden,
    hiddenEmojiCount: data.hiddenEmojiCount,
    engineVersion: data.engineVersion,
    isBirthday: data.isBirthday,
    hasPermission: data.hasPermission,
    origin: data.origin,
    views: data.views,
    generationStatus: data.generationStatus,
    generationTime: data.generationTime,
    tweetUrl: data.tweetUrl,
    waybackMachineUrl: data.waybackMachineUrl,
    waybackMachineCapturedAt: data.waybackMachineCapturedAt,
    waybackMachineTimestamp: data.waybackMachineTimestamp,
    previousWaybackMachineUrl: data.previousWaybackMachineUrl,
    waybackMachineStatus: data.waybackMachineStatus,
    waybackMachineRequestedAt: data.waybackMachineRequestedAt,
    waybackMachineLastAttemptAt: data.waybackMachineLastAttemptAt,
    waybackMachineError: data.waybackMachineError,
    canTriggerWaybackMachine: data.canTriggerWaybackMachine,
    authorViewTime: data.authorViewTime,
    race: data.race,
    participantCount: data.participantCount,
    participantEmojis: data.participantEmojis,
    phase: data.phase,
  };
}
